// Shapes returned by the analytics RPCs (supabase/migrations/
// 20261013100000_analytics_read_model.sql). Numbers arrive as JSON numbers,
// except where Postgres jsonb carries them as text (series values).

import type { ChannelKey } from '../types';

export type AnalyticsPlatform = 'facebook' | 'instagram' | 'linkedin' | 'ga4';
export const SOCIAL_PLATFORMS = ['facebook', 'instagram', 'linkedin'] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

/** The UI's channel keys predate the pipeline: GA is 'google' on screen. */
export function channelOf(platform: AnalyticsPlatform): ChannelKey {
  return platform === 'ga4' ? 'google' : platform;
}
export function platformOf(channel: ChannelKey): AnalyticsPlatform | null {
  if (channel === 'google') return 'ga4';
  if (channel === 'facebook' || channel === 'instagram' || channel === 'linkedin') return channel;
  return null;
}

export const PLATFORM_LABEL: Record<AnalyticsPlatform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  ga4: 'Google Analytics',
};

/** The order platforms are listed in everywhere on the page, after Overview:
 *  the tabs, the status pills and freshness details, and each section's rows. */
export const PLATFORM_ORDER: AnalyticsPlatform[] = ['linkedin', 'instagram', 'facebook', 'ga4'];

export type SyncStatus = 'ok' | 'failed' | 'needs_reauth' | 'not_connected';

export interface SyncInfo {
  platform: AnalyticsPlatform;
  status: SyncStatus;
  reason: string | null;
  last_attempt_at: string | null;
  last_success_at: string | null;
  refreshing: boolean;
  /** The lease's age in seconds by the server's clock at read time; null without one. */
  lease_age_s: number | null;
  retry_after: string | null;
}

export interface SeriesPoint {
  platform: AnalyticsPlatform;
  year: number;
  month: number;
  followers: string | null;
  active_users: string | null;
  account_id: string | null;
  ok: boolean;
  generated_at: string | null;
}

export interface LatestAccount {
  metrics: Record<string, unknown>;
  top_content: Array<{ title: string; path: string | null; views: number }>;
  top_sources: Array<{ source: string; sessions: number; users: number | null }>;
  year: number;
  month: number;
  generated_at: string | null;
}

export type LearningPolarity = 'do' | 'avoid' | 'info';
export type LearningStrength = 'early' | 'consistent';

export interface Learning {
  id: number;
  platform: AnalyticsPlatform;
  kind: string;
  subject: string;
  polarity: LearningPolarity;
  statement: string;
  detail: string | null;
  effect: number | null;
  sample_n: number;
  compare_n: number;
  strength: LearningStrength;
  evidence_post_ids: string[];
  span_start: string | null;
  span_end: string | null;
  /** The split point for threshold kinds (caption_length: the median length). */
  threshold: number | null;
  rank: number;
  computed_at: string;
  /** The posts behind the statement, best first. */
  evidence: EvidencePost[];
}

export interface EvidencePost {
  id: string;
  platform: SocialPlatform;
  title: string | null;
  permalink: string | null;
  thumbnail_url: string | null;
  published_at: string;
  interaction_rate_pct: number | null;
}

export interface Coverage {
  posts: number;
  eligible: number;
  too_new: number;
  low_audience: number;
  no_audience: number;
}

export interface BrandAnalytics {
  brand_id: string;
  can_refresh: boolean;
  connections: Record<AnalyticsPlatform, { connected: true; name: string | null } | null>;
  sync: SyncInfo[];
  series: SeriesPoint[];
  latest: Partial<Record<AnalyticsPlatform, LatestAccount>>;
  learnings: Learning[];
  coverage: Partial<Record<SocialPlatform, Coverage>>;
}

export type WindowName = 'current' | 'previous';

export interface PlatformKpi {
  window: WindowName;
  platform: AnalyticsPlatform;
  posts: number;
  interactions: number | null;
  interaction_rate_pct: number | null;
  reach: number | null;
  impressions: number | null;
  clicks: number | null;
  posts_with_rate: number;
  /** Posts from before every post was on file: the old pipeline's top posts. */
  pre_catalog_posts: number;
}

/** The span every post of a platform's connected account is on file for —
 *  from `since` (null: no complete listing yet) up to `until`, its last
 *  successful listing — and whether each window lies inside it (null: no
 *  comparison window). A window reaching outside holds only part of its posts,
 *  so its totals undercount. */
export interface HeldCoverage {
  since: string | null;
  until: string | null;
  current: boolean;
  previous: boolean | null;
}

export interface RankedPost {
  id: string;
  platform: SocialPlatform;
  permalink: string | null;
  title: string | null;
  thumbnail_url: string | null;
  format: string | null;
  media_type: string | null;
  published_at: string;
  interactions: number | null;
  audience: number | null;
  interaction_rate_pct: number | null;
  platform_median_rate: number;
  relative: number;
  caption_length: number | null;
  hashtag_count: number | null;
  has_link: boolean | null;
}

export interface WindowAnalytics {
  kpis: PlatformKpi[];
  held: Partial<Record<SocialPlatform, HeldCoverage>>;
  best: RankedPost[];
  worst: RankedPost[];
  /** Posts in the window left out of ranking, by reason. */
  excluded: Record<string, number>;
}

export type PostSort = 'recent' | 'rate' | 'interactions' | 'audience';

export interface PostRow {
  id: string;
  platform: SocialPlatform;
  permalink: string | null;
  title: string | null;
  thumbnail_url: string | null;
  format: string | null;
  media_type: string | null;
  published_at: string;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  clicks: number | null;
  video_views: number | null;
  interactions: number | null;
  reach: number | null;
  impressions: number | null;
  audience: number | null;
  interaction_rate_pct: number | null;
  clicks_per_100_impr: number | null;
  eligible: boolean;
  ineligible_reason: string | null;
  metrics_as_of: string | null;
}

/** Why the server did not start a platform's refresh, and until when
 *  (refresh-analytics-for-brand). */
export type RefreshSkipReason = 'in_flight' | 'backing_off' | 'recent';

export interface RefreshResult {
  started: AnalyticsPlatform[];
  skipped: Array<{ platform: AnalyticsPlatform; reason: RefreshSkipReason; until: string | null }>;
}

export interface PostsPage {
  total: number;
  rows: PostRow[];
}

/** A date window as instants: start inclusive, end exclusive (local midnights). */
export interface DateRange {
  start: string;
  end: string;
}

export interface AnalyticsWindow {
  current: DateRange;
  previous: DateRange | null;
}
